import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { VFC } from "react";
import styled from "styled-components";
import { Image, Markdown } from "components";
import { PipelineRoutingLinkButton } from "components/Molecules/PipelineRoutingLinkButton";
import { HomeHeroShape } from "icons";
import { DataTestId } from "types/enums/testId";
import { Hero1Content } from "types/graphql/generated";
import { Hero1Form } from "./Hero1Form";

interface Props {
  className?: string;
  content: Hero1Content;
}

const Hero1: VFC<Props> = withErrorBoundary(
  ({
    content: {
      title,
      descriptionMobile,
      privacyPolicy,
      background,
      backgroundMobile,
      hero1Form,
      mobileButtonLabel,
      mobileButtonLink,
    },
  }) => {
    return (
      <StyledHero id="topComponent">
        <div className="Content">
          <div className="Hero">
            <Markdown className="Hero__title Color_eucalyptus80 Font_136_104_600 md:Font_64_72_600 ">{title}</Markdown>
            <Markdown className="Hero__descriptionMobile Color_secondary Font_24_32_450">{descriptionMobile}</Markdown>

            <Image
              priority
              fill
              quality={100}
              src={background.data.background.src}
              alt={background.data.background.alt}
              className="Hero__background"
              sizes={`(max-width: 768px) 100vw, 90vw `}
            />

            <HomeHeroShape className="Hero__shape" />

            <div className="Hero__form">
              <Hero1Form form={hero1Form} buttonLink={mobileButtonLink} />
            </div>
          </div>
          <PipelineRoutingLinkButton
            className="Hero__mobileButton"
            href={mobileButtonLink}
            dataTestId={DataTestId.HeroSubmitButtonMobile}
          >
            {mobileButtonLabel}
          </PipelineRoutingLinkButton>
          <Markdown className="Hero__privacyPolicy Font_12_16_450">{privacyPolicy}</Markdown>
        </div>
        <div className="Hero__backgroundMobile">
          <Image
            fill
            src={backgroundMobile.data.background.src}
            alt={backgroundMobile.data.background.alt}
            style={{ objectFit: "cover", objectPosition: "center" }}
          />
        </div>
      </StyledHero>
    );
  },
  {}
);

const StyledHero = styled.section`
  position: relative;
  z-index: 1;

  .Hero {
    position: relative;
    display: flex;
    align-items: center;
    flex-direction: column;
    padding-top: 64px;
    padding-bottom: 80px;
    margin-bottom: -226px;
    mix-blend-mode: multiply;
  }

  .Hero__title {
    position: relative;
    text-align: -moz-center;
    z-index: 1;
    mix-blend-mode: multiply;

    p {
      text-align: -webkit-center;
    }

    span {
      display: flex;
      justify-content: space-between;
      max-width: 580px;
    }

    span:not(:first-child) {
      margin-top: 26px;
    }
  }

  .Hero__descriptionMobile {
    display: none;
  }

  .Hero__background {
    object-fit: cover;
  }

  .Hero__shape {
    position: absolute;
    bottom: 0;
    right: 0;
  }

  .Hero__form {
    position: relative;
    margin-top: 268px;
    z-index: 1;
    max-width: 604px;
    width: 100%;
  }

  .Hero__mobileButton {
    display: none;
  }

  .Hero__privacyPolicy {
    position: relative;
    bottom: -202px;
    padding: 0 0 8px 8px;
    color: ${({ theme }) => theme.colors.fog["00"]};
    text-shadow: 0 1px 2px rgb(25 24 23 / 16%);
  }

  .Hero__backgroundMobile {
    display: none;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    .Hero {
      margin-bottom: -216px;
    }

    .Hero__background {
      height: 640px;
    }

    .Hero__privacyPolicy {
      bottom: -180px;
      padding: 0 0 16px 8px;
    }

    .Hero__form {
      margin-top: 208px;
    }

    .Hero__shape {
      display: none;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    .Hero {
      align-items: unset;
      padding-top: 0;
      padding-bottom: 0;
      margin-bottom: 0;
    }

    .Hero__title {
      text-align: unset;
      color: ${({ theme }) => theme.colors.fog["80"]};

      p {
        text-align: unset;
      }

      span {
        display: flex;
        justify-content: unset;
        margin-left: 12px;
        max-width: 100px;
      }

      span:not(:first-child) {
        margin-left: 0;
        margin-top: unset;
        max-width: unset;
      }
    }

    .Hero__descriptionMobile {
      display: flex;
      margin-top: 16px;
    }

    .Hero__background {
      display: none;
    }

    .Hero__privacyPolicy {
      display: none;
    }

    .Hero__form {
      /* margin-top: 32px; */
      display: none;
    }

    .Hero__mobileButton {
      display: flex;
      margin-top: 32px;
      border: none;
    }

    .Hero__backgroundMobile {
      height: 382px;
      position: relative;
      display: block;
      margin-top: 64px;
    }
  }
`;

gql`
  fragment Hero1Content on ComponentHeroHero1 {
    title
    descriptionMobile
    privacyPolicy
    mobileButtonLink
    mobileButtonLabel
    background {
      data {
        background: attributes {
          src: hash
          width
          height
          alt: alternativeText
        }
      }
    }
    backgroundMobile {
      data {
        background: attributes {
          src: hash
          width
          height
          alt: alternativeText
        }
      }
    }
    hero1Form {
      id
      emailLabel
      emailErrorMessage
      buttonLabel
      planType
      prospectEventType
      sourceIdentifier
    }
  }
`;

export { Hero1 };
export type { Props as Hero1Props };
